<template>
    <div ref="rootElement">
        <div class="opPaginationContainer top" v-if="showUpperPagination">
            <div class="opSvuotaFiltri"></div>
            <div class="opPaginationNumberContainer">
                <div class="opPaginationNumber" @click="subtractPage">&lt;</div>
                <div class="opPaginationNumber" v-for="i in pages" :key="i" :class="{selected: selectedPage == i}" @click="setSelectedPage(i)">{{i}}</div>
                <div class="opPaginationNumber" @click="addPage">&gt;</div>
                <select class="opItemsPerPage" v-model="xItemsPerPage">
                    <option :value="10">10</option><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
                </select>
            </div>
            <div class="opItemsTotal">Totale: <span class="opItemsTotalText">{{xData.length}}</span></div>
        </div>
        
        <div class="mb-4" style="width:100%; overflow-x:auto">
            <table class="opTable" ref="tableRoot">
                <slot :items="paginated"></slot>
            </table>
        </div>
        
        <div class="opPaginationContainer bottom" v-if="showLowerPagination">
            <div class="opSvuotaFiltri"></div>
            <div class="opPaginationNumberContainer">
                <div class="opPaginationNumber" @click="subtractPage">&lt;</div>
                <div class="opPaginationNumber" v-for="i in pages" :key="i" :class="{selected: selectedPage == i}" @click="setSelectedPage(i)">{{i}}</div>
                <div class="opPaginationNumber" @click="addPage">&gt;</div>
                <select class="opItemsPerPage" v-model="xItemsPerPage">
                    <option :value="10">10</option><option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
                </select>
            </div>
            <div class="opItemsTotal">Totale: <span class="opItemsTotalText">{{xData.length}}</span></div>
        </div>
    </div>
</template>

<script lang="ts">
import { reactive } from 'vue';
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { OP_TABLE_CTX, type OpTableContext, type SortDir, type ColumnMeta } from './opTable.context';

@Options({
    provide() {
        const self = this as any;
        const ctx: OpTableContext = {
            getFilterValue: (key) => {
                return self.localFilters[key];
            },
            setFilterValue: (key, value, meta?: ColumnMeta) => {
                self.filterMeta[key] = meta || self.filterMeta[key] || {};
                self.localFilters[key] = value;
                self.applyFiltersAndSort();
            },
            getSort: () => { 
                return self.sortState
            },
            setSort: (key?: string, dir: SortDir = '') => {
                self.sortState.key = key;
                self.sortState.dir = dir;
                self.applyFiltersAndSort();
            }
        };
        return { [OP_TABLE_CTX as any]: ctx };
    }
})
export default class OpTableLocal extends Vue {

    @Prop() items!: any[];
    @Prop({ default: true }) showUpperPagination!: boolean;
    @Prop({ default: true }) showLowerPagination!: boolean;
    @Prop({ default: 10 }) itemsPerPage!: number;
    @Prop() opName!: string;
    @Prop({ default: () => ({}) }) filtersFn!: Record<string, (item: any, val: any) => boolean>;
    @Prop() clearFilters!: number;
    
    tableRoot!: HTMLElement;
    allItems: any[] = [];
    xData: any[] = [];
    xItemsPerPage = 10;
    
    // stato filtri/sort locale
    localFilters: Record<string, any> = reactive({});
    filterMeta: Record<string, ColumnMeta> = reactive({});

    sortState = {
        key: <string>'',
        dir: <SortDir>''
    }
    
    // pagination (come prima)
    totalPages = 0; lowerPages = 0; upperPages = 0; pages: number[] = []; selectedPage = 1;
    
    created() {
        this.allItems = (this.items || []).slice();
        this.xData = this.allItems.slice();
        this.xItemsPerPage = this.itemsPerPage;
    }
    
    mounted() {
        // mantieni le classi sul <table> come fai tu
        const rootElement: any = this.$refs.rootElement;
        const classes: string[] = []; rootElement.classList.forEach((x: string) => classes.push(x));
        rootElement.classList.value = '';
        this.tableRoot = this.$refs.tableRoot as HTMLElement;
        classes.forEach(x => this.tableRoot.classList.add(x));
        
        this.applyFiltersAndSort();
        this.calcPagination();
    }
    
    @Watch('items.length') onItemsChange() {
        this.allItems = (this.items || []).slice();
        this.applyFiltersAndSort();
        this.calcPagination();
    }
    
    @Watch('xItemsPerPage') onIPPChange() { this.calcPagination(); }
    @Watch('clearFilters') onClearFilters() {
        Object.keys(this.localFilters).forEach(k => (this.localFilters[k] = this.filterMeta[k]?.filterType === 'checkbox' ? 0 : ''));
        this.applyFiltersAndSort();
    }
    
    // exposed per il wrapper per salvare su Storage
    getCurrentFilters() {
        return JSON.parse(JSON.stringify(this.localFilters));
    }
    
    // —————————— core ——————————
    applyFiltersAndSort() {
        // 1) filtri
        let data = this.allItems.slice();
        for (const key of Object.keys(this.localFilters)) {
            const meta = this.filterMeta[key] || {};
            const val = this.localFilters[key];
            
            if (this.filtersFn[key]) {
                if (val == null || val === '' || (typeof val === 'object' && !val.from && !val.to)) continue;
                data = data.filter(item => this.filtersFn[key](item, val));
                continue;
            }
            
            data = data.filter(item => {
                const propVal = key.split('.').reduce((acc, k) => (acc != null ? acc[k] : acc), item);
                switch (meta.filterType) {
                    case 'checkbox': {
                        // 0 ignore, 1 => false, 2 => true
                        if (val === 0 || val === '0' || val === '' || val == null) return true;
                        if (val === 1 || val === '1') return propVal == false;
                        if (val === 2 || val === '2') return propVal == true;
                        return true;
                    }
                    case 'dateRange': {
                        const from = val?.from ? new Date(val.from).toISOString() : null;
                        const to   = val?.to   ? new Date(val.to).toISOString()   : null;
                        if (!from && !to) return true;
                        const p = propVal ? new Date(propVal).toISOString() : null;
                        if (!p) return false;
                        if (from && p < from) return false;
                        if (to && p > to) return false;
                        return true;
                    }
                    case 'numberRange': {
                        const from = (val?.from ?? '') === '' ? null : Number(val.from);
                        const to   = (val?.to ?? '')   === '' ? null : Number(val.to);
                        if (from == null && to == null) return true;
                        const num = Number(propVal);
                        if (Number.isNaN(num)) return false;
                        if (from != null && num < from) return false;
                        if (to != null && num > to) return false;
                        return true;
                    }
                    case 'select':
                        const s = (val ?? '').toString().trim().toLowerCase();
                        if (!s) return true;
                        if (propVal == null && propVal !== false) return false;
                        return propVal.toString().toLowerCase() == s;
                    case 'date':
                    case 'number':
                    case 'text':
                    default: {
                        const s = (val ?? '').toString().trim().toLowerCase();
                        if (!s) return true;
                        if (propVal == null && propVal !== false) return false;
                        return propVal.toString().toLowerCase().includes(s);
                    }
                }
            });
        }
        
        // 2) sort
        if (this.sortState.key && this.sortState.dir) {
            const key = this.sortState.key;
            const dir = this.sortState.dir;
            data.sort((a, b) => {
                let av = key.split('.').reduce((acc, k) => (acc != null ? acc[k] : acc), a);
                let bv = key.split('.').reduce((acc, k) => (acc != null ? acc[k] : acc), b);
                if (av == null) av = ''; if (bv == null) bv = '';
                const res = av.toString().localeCompare(bv.toString(), undefined, { numeric: true, sensitivity: 'base' });
                return dir === 'asc' ? res : dir === 'desc' ? -res : 0;
            });
        }
        
        this.xData = data;
        this.calcPagination();
    }
    
    // ————— pagination (identica alla tua) —————
    setSelectedPage(i: number){ this.selectedPage = i; this.calcPagination(); }
    subtractPage(){ if(this.selectedPage > 1){ this.selectedPage--; this.calcPagination(); } }
    addPage(){ if(this.selectedPage < this.totalPages){ this.selectedPage++; this.calcPagination(); } }
    
    calcPagination(){
        this.totalPages = Math.ceil(this.xData.length / this.xItemsPerPage) || 1;
        if (this.selectedPage > this.totalPages) this.selectedPage = this.totalPages;
        
        this.lowerPages = this.selectedPage - 2;
        this.upperPages = this.selectedPage + 2;
        
        if (this.selectedPage == 1) this.upperPages += 2;
        else if (this.selectedPage == 2) this.upperPages += 1;
        
        if (this.selectedPage == this.totalPages) this.lowerPages -= 2;
        else if (this.selectedPage == this.totalPages - 1) this.lowerPages -= 1;
        
        if (this.upperPages > this.totalPages) this.upperPages = this.totalPages;
        if (this.lowerPages <= 0) this.lowerPages = 1;
        
        this.pages = [];
        for (let i = this.lowerPages; i <= this.upperPages; i++) this.pages.push(i);
    }
    
    get paginated() {
        const start = this.xItemsPerPage * (this.selectedPage - 1);
        return this.xData.slice(start, start + this.xItemsPerPage) || [];
    }
}
</script>
